/**
 * Pure share and demo link builders, shared by the end panel (webpack) and the
 * Worker (wrangler bundles it). No DOM: only string work and URL. The share post's
 * words live in data/shareCopy.json so marketing can change them without code.
 */
import shareCopy from '../data/shareCopy.json' with { type: 'json' };

export const GAME_ORIGIN = 'https://receipt-chase.letsweel.com';
export const DOGS = ['summer', 'winston', 'kevin', 'pickles'];

/** The channels a share link can come from. */
export const VIA = ['linkedin', 'x', 'copy', 'native'];
const RUN_ID = /^[0-9a-f]{12}$/;
const GENERIC_ID = /^([a-z]+)-(?:(win)|l([1-6]))$/;

/** Book a demo URL; the end panel opens it in a new tab. */
export function demoUrl() {
  return 'https://letsweel.com/demo?utm_source=receipt_chase&utm_medium=game&utm_campaign=receipt_chase&utm_content=end_screen';
}

/** Clamp a level to the integer range 1-6. */
export function clampLevel(n) {
  const level = Math.floor(Number(n));
  if (!Number.isFinite(level)) return 1;
  return Math.min(6, Math.max(1, level));
}

/** 12345 -> "12,345". Done by hand so Worker and browser agree on locale. */
export function formatNumber(n) {
  const v = Math.max(0, Math.round(Number(n) || 0));
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** Escape a value for use in HTML text or a double-quoted attribute. */
export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * The id for a share URL: the run's recorded share_id, or a generic card id
 * (`<dog>-win` or `<dog>-l<N>`) when the run was not recorded.
 */
export function shareId({ shareId: id, dog, won, levelReached } = {}) {
  if (typeof id === 'string' && RUN_ID.test(id)) return id;
  return won ? `${dog}-win` : `${dog}-l${clampLevel(levelReached)}`;
}

/** `<origin>/s/<id>?via=<via>`; via is linkedin, x, copy or native. */
export function shareUrl({ origin = GAME_ORIGIN, id, via }) {
  if (!VIA.includes(via)) throw new Error(`Unknown share channel: ${via}`);
  return `${origin}/s/${encodeURIComponent(id)}?via=${via}`;
}

/**
 * The origin share links point at. On one of the Worker's own hosts (production, or a
 * branch Preview such as the playtest) it is that host, so a share made there previews
 * from the same deploy; anywhere else (localhost, tests) it is production.
 */
export function shareOrigin(origin) {
  try {
    const url = new URL(origin);
    const host = url.hostname;
    // receipt-chase is the Worker's name from launch; receipt-hunt hosts are
    // still served until the old address redirects.
    const ours = ['receipt-chase', 'receipt-hunt'].some((name) =>
      host === `${name}.divipay.workers.dev` || host.endsWith(`-${name}.divipay.workers.dev`));
    if (url.protocol === 'https:' && ours) {
      return url.origin;
    }
  } catch (e) {
    // not a URL: production
  }
  return GAME_ORIGIN;
}

/** LinkedIn's link-only share dialog (it takes no text). */
export function linkedInUrl(url) {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
}

/** Fill `{name}` placeholders; an unknown name is left as typed. */
function fill(template, values) {
  return template.replace(/\{(\w+)\}/g, (m, k) => (k in values ? String(values[k]) : m));
}

/**
 * The share post, from data/shareCopy.json (or `copy`, for tests). `dog` picks the breed word,
 * `won` and `level` pick the dog line (level 1 has its own). `rank` is the run's place on the
 * board, only when the server recorded it; without one the unranked line is used. `url` is the
 * share link; with no url the post ends without it (the phone share sheet passes the link
 * separately).
 */
export function sharePost({ rank, score, url, dog, won, level }, copy = shareCopy) {
  const n = Math.floor(Number(rank));
  const ranked = Number.isFinite(n) && n >= 1;
  const lv = clampLevel(level);
  const values = {
    rank: ranked ? n : '',
    points: formatNumber(score),
    level: lv,
    breed: (copy.breeds || {})[dog] || 'dog',
    url: url || '',
  };
  const dogKey = won ? 'win' : (lv <= 1 ? 'loss1' : 'loss');
  const resultKey = `${won ? 'win' : 'loss'}${ranked ? 'Ranked' : 'Unranked'}`;
  values.dogLine = fill((copy.dogLine || {})[dogKey] || '', values);
  values.resultLine = fill((copy.resultLine || {})[resultKey] || '', values);
  const post = copy.post.map((p) => fill(p, values).replace(/ {2,}/g, ' ').trim()).filter(Boolean).join('\n\n');
  // with no link the call to action would end on a dangling colon
  return url ? post : post.replace(/:$/, '.');
}

/**
 * LinkedIn's post box with the post already typed in. The URL is undocumented and can
 * stop working (the phone apps may open an empty post), so the button also copies the
 * post text for the player to paste.
 */
export function linkedInPostUrl(post) {
  return `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(post)}`;
}

/** X's post box with the post typed in; the post carries its own link. */
export function xIntentUrl(post) {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(post)}`;
}

/**
 * Parse a path id from /s/<id>. A run id is 12 lower-case hex characters; a
 * generic id is `<dog>-win` or `<dog>-l<1-6>`. Anything else is null.
 */
export function parseShareId(id) {
  if (typeof id !== 'string') return null;
  if (RUN_ID.test(id)) return { kind: 'run', id };
  const m = GENERIC_ID.exec(id);
  if (!m || !DOGS.includes(m[1])) return null;
  if (m[2]) return { kind: 'generic', dog: m[1], won: true, levelReached: 6 };
  return { kind: 'generic', dog: m[1], won: false, levelReached: Number(m[3]) };
}

/** Add the share UTMs to the landing page URL, keeping any existing query. */
export function landingUrl(base, { via, dog, won }) {
  const url = new URL(base);
  url.searchParams.set('utm_source', via);
  url.searchParams.set('utm_medium', 'social_share');
  url.searchParams.set('utm_campaign', 'receipt_chase');
  url.searchParams.set('utm_content', `${dog}_${won ? 'win' : 'loss'}`);
  return url.toString();
}

/** Read a share_run row, which arrives snake_case from Supabase. */
function runFields(run) {
  return {
    won: !!run.won,
    level: clampLevel(run.level_reached ?? run.levelReached),
    score: run.score,
    receipts: run.receipts,
  };
}

/** OG title; `run` is null for the generic card. */
export function ogTitle(run) {
  if (!run) return 'Receipt Chase, a game by Weel';
  const { won, level, score } = runFields(run);
  const points = formatNumber(score);
  if (won) return `★ We cleared all 6 levels of Receipt Chase with ${points} points`;
  return `We got to level ${level} of Receipt Chase with ${points} points`;
}

/** OG description; `run` is null for the generic card. "We" is the player and their dog, named when known. */
export function ogDescription(run) {
  if (!run) return 'Pick one of four dogs and catch the runaway receipts before the clock runs out. 6 levels, 1 leaderboard. Can you top it?';
  const we = DOGS.includes(run.dog) ? `${run.dog[0].toUpperCase()}${run.dog.slice(1)} and I` : 'We';
  return `${we} caught ${formatNumber(runFields(run).receipts)} receipts. Can you beat us?`;
}
